import * as vp from 'vopay';
import { useEffect, useState } from 'react';
import axios, { CancelTokenSource } from 'axios';

import { useBlockingLoader } from 'shared';
import vopayApi from '../../services/api.config';

type HookProps = {
  api: vp.Api;
  bearer?: string;
  isBackground?: boolean;
};

type ReturnTuple = [
  vp.TransactionTypesResponse | undefined,
  (requestParams: vp.TransactionTypesRequst) => void,
  () => void,
];

// Pages fetching transaction types get the fullscreen blocking loader; background callers
// (ApprovalWorkflowProvider's app-load fetch) pass isBackground to fetch without blocking the UI.
function useFetchTransactionTypes({ api, bearer, isBackground }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<vp.TransactionTypesRequst>();
  const [response, setResponse] = useState<vp.TransactionTypesResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);
  const blockingLoader = useBlockingLoader();

  useEffect(() => {
    const sendRequest = async (requestParams: vp.TransactionTypesRequst, cancelTokenSource: CancelTokenSource) => {
      if (!isBackground) blockingLoader.show();

      try {
        const { CountriesID, ...filteredParams } = requestParams;
        const res = await api.fetchTransactionTypes(filteredParams, bearer, cancelTokenSource);

        if (CountriesID) {
          res.TransactionTypes = res.TransactionTypes.filter(
            (type) => type.ADRCountries_ID === String(CountriesID) || type.ADRCountries_ID === null,
          );
        }

        setResponse(res);
        setSendRequest(false);
      } finally {
        if (!isBackground) blockingLoader.hide();
      }
    };

    if (params && shouldSendRequest) void sendRequest(params, source);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams: vp.TransactionTypesRequst) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse(undefined);
      setSendRequest(false);
    },
  ];
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(useFetchTransactionTypes);
